// src/components/questionanswer/QnASQL.jsx
import React, { useEffect, useMemo, useState } from "react";
import beginnerQuestions from "./qnaJSONdata/QnAsql.json";
import experiencedQuestions from "./qnaJSONdata/QnAExperiencedSQL.json";
import QnAsqlScenarioProblems from "./qnaJSONdata/QnAsqlScenarioProblems.json";
import QnASideBarRight from "./QnASideBarRight";
import ScrollButton from "../../ScrollButton";

/**
 * QnASQL — inline Q&A with LEFT TOC + right sidebar + keyword filter
 * - LEFT: sticky "On this page" + Sponsored ad
 * - MAIN: Beginner, Experienced, Scenarios + filter; header has Download PDF + Print
 * - RIGHT: existing sidebar (already has a Sponsored slot)
 */

const SQL_PDF = "/pdfs/sql-interview-questions.pdf"; // ensure the file exists in /public/pdfs/
const normalize = (s = "") => s.toLowerCase();

// ============== AdSense helpers (same pattern as Java/React) ==============
const ADSENSE_CLIENT = "ca-pub-9033879555371063"; // <-- your AdSense client id

function useAdSense(clientId) {
  useEffect(() => {
    if (!clientId) return;
    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch {}
    }
  }, [clientId]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
}) {
  useEffect(() => {
    try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch {}
  }, []);
  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

// ============== UI bits ==============
function FilterBar({ value, onChange, total, shown }) {
  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      <input
        className="form-control form-control-sm"
        style={{ maxWidth: 420 }}
        type="search"
        placeholder="Filter (e.g., joins, window functions, indexing, normalization)…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Filter questions"
      />
      <span className="text-muted small">Showing {shown} of {total}</span>
    </div>
  );
}

function QAInlineList({ items }) {
  return (
    <ol className="ps-3">
      {items.map((it, idx) => (
        <li key={it.id ?? idx} className="mb-3">
          <h5 className="mb-2">{it.question}</h5>
          <div className="text-body" style={{ whiteSpace: "pre-wrap" }}>{it.answer}</div>
          {it.code ? (
            <pre className="mt-2 mb-0" style={{ overflow: "auto" }}>
              <code>{it.code}</code>
            </pre>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function OnThisPageLeft({ pdfHref }) {
  return (
    <div className="card shadow-sm border-0">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">On this page</h2>
        <nav className="nav flex-column small">
          <a className="nav-link px-0" href="#beginner">Beginner Q&amp;A</a>
          <a className="nav-link px-0" href="#experienced">Experienced Q&amp;A</a>
          <a className="nav-link px-0" href="#scenarios">Scenario-Based Q&amp;A</a>
          <a
            className="nav-link px-0 mt-2 text-decoration-underline"
            href={pdfHref}
            download="TechVenture-SQL-Interview-Questions.pdf"
            aria-label="Download SQL interview questions PDF"
          >
            ⬇︎ Download PDF
          </a>
        </nav>
      </div>
    </div>
  );
}

function LeftSponsoredCard() {
  return (
    <div className="card shadow-sm border-0 mt-3">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">Sponsored</h2>
        <AdSlot adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

function TopActions({ pdfHref }) {
  return (
    <div className="d-flex gap-2">
      <a
        href={pdfHref}
        download="TechVenture-SQL-Interview-Questions.pdf"
        className="btn btn-outline-danger btn-sm"
        aria-label="Download SQL interview questions as PDF"
      >
        Download PDF
      </a>
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm"
        onClick={() => window.print()}
        aria-label="Print this page"
      >
        Print
      </button>
    </div>
  );
}

// ============== Main ==============
export default function QnASQL() {
  useAdSense(ADSENSE_CLIENT);

  const [query, setQuery] = useState("");

  const totalCount =
    (beginnerQuestions?.length || 0) +
    (experiencedQuestions?.length || 0) +
    (QnAsqlScenarioProblems?.length || 0);

  const filteredBeg = useMemo(() => {
    const qn = normalize(query);
    if (!qn) return beginnerQuestions;
    return beginnerQuestions.filter((x) =>
      normalize(`${x.question} ${x.answer} ${x.code ?? ""}`).includes(qn)
    );
  }, [query]);

  const filteredExp = useMemo(() => {
    const qn = normalize(query);
    if (!qn) return experiencedQuestions;
    return experiencedQuestions.filter((x) =>
      normalize(`${x.question} ${x.answer} ${x.code ?? ""}`).includes(qn)
    );
  }, [query]);

  const filteredScn = useMemo(() => {
    const qn = normalize(query);
    if (!qn) return QnAsqlScenarioProblems;
    return QnAsqlScenarioProblems.filter((x) =>
      normalize(`${x.question} ${x.answer} ${x.code ?? ""}`).includes(qn)
    );
  }, [query]);

  const shownCount =
    (filteredBeg?.length || 0) +
    (filteredExp?.length || 0) +
    (filteredScn?.length || 0);

  return (
    <div className="container-fluid">
      <div className="row g-4 flex-xl-nowrap">
        {/* LEFT: On This Page + Sponsored (sticky) */}
        <aside className="d-none d-xl-block col-xl-2 pt-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <OnThisPageLeft pdfHref={SQL_PDF} />
            <LeftSponsoredCard />
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 pt-3" role="main">
          <header className="mb-2 d-flex justify-content-between align-items-start">
            <div>
              <h1 className="h2 mb-1">SQL Interview Questions</h1>
              <p className="text-muted mb-0">
                Curated questions for beginner, experienced, and scenario-based interviews.
                Use the filter to jump to what you need.
              </p>
            </div>
            <TopActions pdfHref={SQL_PDF} />
          </header>

          <FilterBar value={query} onChange={setQuery} total={totalCount} shown={shownCount} />

          {/* Beginner */}
          <section id="beginner" className="pt-2">
            <h2 className="h4 mt-3">Top SQL Interview Questions for Entry-Level Candidates</h2>
            <p className="text-body-secondary">
              Fundamentals such as SELECT/WHERE, joins, aggregations, GROUP BY/HAVING,
              constraints, and basic indexing/normalization.
            </p>
            <QAInlineList items={filteredBeg} />
          </section>

          {/* In-article ad (optional) */}
          <div className="my-3 text-center">
            <AdSlot
              adSlot="5201219465"
              style={{ display: "block", minHeight: 90, textAlign: "center" }}
            />
          </div>

          {/* Experienced */}
          <section id="experienced" className="pt-2">
            <h2 className="h4 mt-4">Top SQL Interview Questions for Experienced Candidates</h2>
            <p className="text-body-secondary">
              Advanced topics like window functions, CTEs/recursion, query planning,
              performance tuning, partitioning, transactions/isolation, and security.
            </p>
            <QAInlineList items={filteredExp} />
          </section>

          {/* Scenarios */}
          <section id="scenarios" className="pt-2">
            <h2 className="h4 mt-4">Scenario-Based SQL Interview Questions</h2>
            <p className="text-body-secondary">
              Hands-on problems with sample solutions and SQL snippets. Try writing a solution
              before expanding the provided answer.
            </p>
            <QAInlineList items={filteredScn} />
          </section>

          <footer className="border-top mt-4 pt-3 small text-muted">
            Tip: When explaining queries, mention indexes used, estimated row counts,
            and trade-offs (e.g., window vs. subquery) for performance clarity.
          </footer>

          <ScrollButton />
        </main>

        {/* RIGHT: existing sidebar */}
        <aside className="col-12 col-lg-4 col-xl-3 pt-3 border-start">
          <QnASideBarRight />
        </aside>
      </div>
    </div>
  );
}
